import { Controller } from '@hotwired/stimulus';
import { postGeneratorOpenAI, getPaywallController } from '../lib/generator_api';
import {
  applyDisplayFormula,
  bindFormulaDelimiterListener,
  delimiterPromptInstruction,
  processGeneratedFormula,
  unbindFormulaDelimiterListener,
} from '../lib/generator_formula_delimiter';

export default class extends Controller {
  static targets = [
    'usecase',
    'texttoreplace',
    'cell',
    'submit',
    'submitLabel',
    'placeholder',
    'loading',
    'output',
    'formulaText',
    'error',
    'successNote',
    'copyButton'
  ];

  static values = {
    tool: { type: String, default: 'regexreplacer' }
  };

  connect() {
    bindFormulaDelimiterListener(this);
    this.formulaValue = '';
  }

  disconnect() {
    unbindFormulaDelimiterListener(this);
  }

  paywall() {
    return getPaywallController(this.element);
  }

  parseExample(event) {
    const raw = event.currentTarget.dataset.example;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      console.error('Invalid example payload');
      return null;
    }
  }

  loadExample(event) {
    const data = this.parseExample(event);
    if (!data) return;

    this.usecaseTarget.value = data.usecase;
    this.texttoreplaceTarget.value = data.sample;
    this.cellTarget.value = 'A1';
    this.clearError();
    this.hideResult();
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
    this.usecaseTarget.focus();
  }

  async applyFormula(event) {
    const data = this.parseExample(event);
    if (!data?.formula) return;

    const { canonical, display } = processGeneratedFormula(data.formula);
    this.canonicalFormula = canonical;
    this.formulaValue = display;
    await this.copyToClipboard(display);
    this.showResult(display);
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
  }

  highlightExampleCard(button) {
    this.element.querySelectorAll('[data-example-card]').forEach((card) => {
      card.classList.remove('ring-2', 'ring-emerald-500/60', 'ring-offset-2');
    });
    button.closest('[data-example-card]')?.classList.add('ring-2', 'ring-emerald-500/60', 'ring-offset-2');
  }

  scrollToGenerator() {
    document.getElementById('regexreplace-generator')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const usecase = this.usecaseTarget.value.trim();
    const texttoreplace = this.texttoreplaceTarget.value.trim();

    if (!usecase || !texttoreplace) {
      this.showError('Describe your use case and add an example string.');
      return;
    }

    const cellRaw = this.cellTarget.value;
    const cellValue = !cellRaw || cellRaw.trim() === '' ? 'A1' : cellRaw.trim();

    const userRole =
      'You are helping to figure out the regular expression needed to replace a string of text. Specifically need to generate a regereplace() formula for the user. Only include the regular expression and replacement, do not explain your response.';
    const prompt =
      String.raw`Here are examples: 
    Use Case: Replace word with replacement.
    Text to Replace: the word word in this sentance.
    Range: A1
    Response: =REGEXREPLACE(A1,"word","replacement")
    Use Case: Remove extra spaces.
    Text to Replace:multiple  spaces with a  single space in cell A1.
    Response: =REGEXREPLACE(A1, "\s+", " ")
    Use Case: Remove HTML Tags.
    Text to Replace: <p>Some text</p> <ul> <li>Item 1</li> <li>Item 2</li> </ul>
    Range: A1.
    Response: =REGEXREPLACE(A1, "<.*?>", "")
    Use Case: Replace Line Breaks with a comma.
    Text to Replace: Line 1 \n Line 2 \n Line 3
    Response: =REGEXREPLACE(A1, "\n", ", ")
    Use Case: ` +
      usecase +
      ' Text to replace: ' +
      texttoreplace +
      ' Range: ' +
      cellValue +
      delimiterPromptInstruction() +
      ' Response: ';

    this.setLoading(true);
    this.hideResult();

    try {
      const result = await postGeneratorOpenAI({
        userRole,
        prompt,
        generator: this.toolValue
      });

      if (result.limited) {
        this.paywall()?.handleLimitReached();
        return;
      }

      if (result.error) {
        console.error('Regex replacer proxy error', result.response?.status, result.detail);
        this.showError('Could not generate a formula. Try again in a moment.');
        return;
      }

      const data = result.data;
      const rawFormula = data.choices?.[0]?.message?.content?.trim();

      if (!rawFormula) {
        console.error('OpenAI proxy returned no formula', data);
        this.showError('No formula came back. Tweak your description and try again.');
        return;
      }

      const { canonical, display } = processGeneratedFormula(rawFormula);
      this.canonicalFormula = canonical;
      this.formulaValue = display;
      await this.copyToClipboard(display);
      this.paywall()?.applyUsageFromResponse(data.generator_usage);
      this.showResult(display);

      if (data.generator_usage?.remaining === 1) {
        this.showSuccessNudge();
      }
      if (data.generator_usage?.remaining === 0) {
        this.paywall()?.handleLimitReached();
      }
    } catch (err) {
      console.error('Regex replacer request failed', err);
      this.showError('Connection failed. Check your network and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async copy(event) {
    event?.preventDefault();
    if (!this.formulaValue) return;

    await this.copyToClipboard(this.formulaValue);
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied again.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be blocked; formula is still visible in the output panel.
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      const paywall = this.paywall();
      const atLimit = paywall && !paywall.paidMemberValue && paywall.remainingValue === 0;
      if (atLimit) {
        paywall.syncUi(0);
      } else {
        this.submitTarget.disabled = isLoading;
      }
    }
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isLoading ? 'Generating…' : 'Generate';
    }
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle('hidden', !isLoading);
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.toggle('hidden', isLoading);
    if (isLoading && this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (isLoading && this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (isLoading && this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  hideResult() {
    if (this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  showResult(formula) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    applyDisplayFormula(this, formula);
    if (this.hasOutputTarget) this.outputTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.remove('hidden');
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied to clipboard. Paste into your sheet.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showSuccessNudge() {
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Nice. 1 free generation left on this tool.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove('hidden');
    }
    this.hideResult();
  }
}
